import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';

const emptyForm = {
  email: '',
  firstName: '',
  lastName: '',
  programId: '',
  roleId: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddNewUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  programOptions,
  defaultProgramId,
  roleOptions,
}) => {
  const isModify = Boolean(user);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (user) {
      setFormData({
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        programId: user.programId || '',
        roleId: user.roleId || '',
      });
    } else {
      emptyForm.programId = defaultProgramId;
      setFormData(emptyForm);
    }

    setErrors({});
  }, [isOpen, user]);

  const title = useMemo(() => (isModify ? 'Modify User' : 'Add New User'), [isModify]);

  const filteredRoleOptions = useMemo(() => {
    if (formData.programId !== 'CDC') {
      return roleOptions.filter((role) => role.roleName !== 'SYSADMIN');
    }
    return roleOptions;
  }, [formData.programId, roleOptions]);

  const validate = () => {
    const nextErrors = {};
    const requiredFields = {
      email: 'Email',
      firstName: 'First name',
      lastName: 'Last name',
      programId: 'Program ID',
      roleId: 'Role',
    };

    Object.entries(requiredFields).forEach(([field, label]) => {
      const value = `${formData[field] ?? ''}`.trim();
      if (!value) {
        nextErrors[field] = `${label} is required.`;
      }
    });

    if (!nextErrors.email && !EMAIL_REGEX.test(formData.email.trim())) {
      nextErrors.email = 'Enter a valid email address.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onFieldChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value = field === 'email' ? rawValue.toUpperCase() : rawValue;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'programId' && value !== 'CDC') {
        const selectedRole = roleOptions.find((r) => r.id === prev.roleId);
        if (selectedRole?.roleName === 'SYSADMIN') {
          next.roleId = '';
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit({
      ...formData,
      email: formData.email.trim().toUpperCase(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      programId: formData.programId,
      roleId: formData.roleId,
    }, isModify);
  };

  return (
    <Modal
      className="modal"
      overlayClassName="modalOverlay"
      aria-modal="true"
      contentLabel={title}
      shouldCloseOnOverlayClick={true}
      isOpen={isOpen}
      onRequestClose={onClose}>
      <div className="modalHeader">{title}</div>
      <div className="modalContent">
        <div className="userFormGrid">
          <TextField
            fullWidth
            label="User Email"
            value={formData.email}
            onChange={onFieldChange('email')}
            error={Boolean(errors.email)}
            helperText={errors.email}
            disabled={isModify}
            required
          />
          <TextField
            fullWidth
            label="First Name"
            value={formData.firstName}
            onChange={onFieldChange('firstName')}
            error={Boolean(errors.firstName)}
            helperText={errors.firstName}
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            value={formData.lastName}
            onChange={onFieldChange('lastName')}
            error={Boolean(errors.lastName)}
            helperText={errors.lastName}
            required
          />
          <FormControl fullWidth error={Boolean(errors.programId)} required>
            <InputLabel id="program-id-select-label">Program ID</InputLabel>
            <Select
              labelId="program-id-select-label"
              label="Program ID"
              value={formData.programId}
              onChange={onFieldChange('programId')}>
              {programOptions.map((program) => (
                <MenuItem key={program.shortName} value={program.shortName}>
                  {program.shortName}
                </MenuItem>
              ))}
            </Select>
            {errors.programId && <small className="userFormFieldError">{errors.programId}</small>}
          </FormControl>
          <FormControl fullWidth error={Boolean(errors.roleId)} required>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              label="Role"
              value={formData.roleId}
              onChange={onFieldChange('roleId')}
            >
              {filteredRoleOptions.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.roleName}
                </MenuItem>
              ))}
            </Select>
            {errors.roleId && <small className="userFormFieldError">{errors.roleId}</small>}
          </FormControl>
        </div>
      </div>
      <div className="modalButtons">
        <button className="modalButton primaryButton" onClick={handleSubmit}>
          {isModify ? 'Save Changes' : 'Create User'}
        </button>
        <button className="modalButton" onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
};



export default AddNewUserModal;